// ---------------------------------------------------------------------------
// CustomPricesStep — Number inputs for custom tariff pricing
// ---------------------------------------------------------------------------
// Options grouped by metadata.component_name. Editable slots get a number
// input. Formula-driven slots (e.g. spot price) are shown as read-only info
// rows — the user does not enter a value for them. TOU badge shown when
// metadata.has_tou is true. All editable values must be valid numbers ≥ 0
// before Next is enabled.
// ---------------------------------------------------------------------------

import { memo, useCallback, useMemo, useState } from 'react';
import { StepHeading } from '@/components/shared/StepHeading';
import { FooterActions } from '@/components/shell/FooterActions';
import { t } from '@/i18n';
import type { StepOption } from '@/api/types';
import { useCollectedData } from '@/hooks/useCollectedData';
import type { StepProps } from '@/types/steps';
import { CURRENCY_SYMBOLS } from '@/utils/constants';
import { getStepIcon } from '@/utils/stepIcons';
import styles from './CustomPricesStep.module.css';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PriceGroup {
  componentName: string;
  slots: StepOption[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function groupByComponent(options: StepOption[]): PriceGroup[] {
  const map = new Map<string, PriceGroup>();

  for (const opt of options) {
    const meta = opt.metadata;
    const name = typeof meta['component_name'] === 'string' ? meta['component_name'] : 'Other';

    let group = map.get(name);
    if (!group) {
      group = { componentName: name, slots: [] };
      map.set(name, group);
    }
    group.slots.push(opt);
  }

  return [...map.values()];
}

function getSlotId(meta: Record<string, unknown>, fallbackId: string): string {
  return typeof meta['price_template_id'] === 'string' ? meta['price_template_id'] : fallbackId;
}

function getComponentId(meta: Record<string, unknown>): string {
  return typeof meta['component_template_id'] === 'string' ? meta['component_template_id'] : '';
}

function getUnitHint(meta: Record<string, unknown>): string | null {
  const code = typeof meta['currency'] === 'string' ? meta['currency'] : null;
  const unitLabel = typeof meta['unit_label'] === 'string' ? meta['unit_label'] : null;
  if (!code && !unitLabel) return null;

  // Use sub-unit name when backend signals display_in_subunit
  const useSubunit = meta['display_in_subunit'] === true;
  const subunitName = typeof meta['subunit_name'] === 'string' ? meta['subunit_name'] : null;
  const currencyLabel = useSubunit && subunitName
    ? subunitName
    : code ? (CURRENCY_SYMBOLS[code] ?? code) : null;

  return [currencyLabel, unitLabel].filter(Boolean).join(' ');
}

/**
 * Convert a main-unit value to sub-unit for display (e.g. DKK → øre).
 * Returns the original value when display_in_subunit is false.
 */
function toDisplayValue(value: number, meta: Record<string, unknown>): number {
  if (meta['display_in_subunit'] !== true) return value;
  const factor = typeof meta['subunit_factor'] === 'number' ? meta['subunit_factor'] : 1;
  return value * factor;
}

/**
 * Convert a sub-unit display value back to main-unit for submission.
 */
function fromDisplayValue(value: number, meta: Record<string, unknown>): number {
  if (meta['display_in_subunit'] !== true) return value;
  const factor = typeof meta['subunit_factor'] === 'number' ? meta['subunit_factor'] : 1;
  return value / factor;
}

/** True when the slot requires user-entered price_value (no formula). */
function isEditableSlot(opt: StepOption): boolean {
  return opt.metadata['has_formula'] !== true;
}

// ---------------------------------------------------------------------------
// CustomPricesStep
// ---------------------------------------------------------------------------

export const CustomPricesStep = memo(function CustomPricesStep({
  stepId,
  options,
  onSubmit,
  isSubmitting,
}: StepProps) {
  const groups = useMemo(() => groupByComponent(options.options), [options.options]);
  const editableOptions = useMemo(
    () => options.options.filter(isEditableSlot),
    [options.options],
  );

  const collectedData = useCollectedData();

  // Pre-fill VAT toggle from collected_data (default ON).
  // TODO(per-component-vat): currently a single global toggle. If templates
  // mix components with different VAT rates and users need to distinguish,
  // render per-component VAT badges and/or per-component toggles. Backend
  // (`create_custom_tariff`) already strips VAT per-component, so only the
  // UI would need to change.
  const [vatIncluded, setVatIncluded] = useState<boolean>(() => {
    const ret = collectedData['retailer'];
    if (typeof ret !== 'object' || ret === null) return true;
    const ct = (ret as Record<string, unknown>)['custom_tariff'];
    if (typeof ct !== 'object' || ct === null) return true;
    const pi = (ct as Record<string, unknown>)['price_input'];
    if (typeof pi !== 'object' || pi === null) return true;
    const stored = (pi as Record<string, unknown>)['vat_included'];
    return typeof stored === 'boolean' ? stored : true;
  });

  // Pre-fill from collected_data (back-navigation)
  const [values, setValues] = useState<Record<string, string>>(() => {
    const ret = collectedData['retailer'];
    if (typeof ret !== 'object' || ret === null) return {};
    const ct = (ret as Record<string, unknown>)['custom_tariff'];
    if (typeof ct !== 'object' || ct === null) return {};
    const pi = (ct as Record<string, unknown>)['price_input'];
    if (typeof pi !== 'object' || pi === null) return {};
    const piObj = pi as Record<string, unknown>;
    const restored: Record<string, string> = {};

    // Only restore values for editable (non-formula) slots
    const editable = options.options.filter(isEditableSlot);

    const comps = piObj['components'];
    if (Array.isArray(comps)) {
      // Multi-component format: components → [{ id, prices/price_value }]
      for (const comp of comps) {
        if (typeof comp !== 'object' || comp === null) continue;
        const c = comp as Record<string, unknown>;
        const cid = typeof c['id'] === 'string' ? c['id'] : '';
        const cpv = c['price_value'];
        if (typeof cpv === 'number') {
          const match = editable.find((o) => getComponentId(o.metadata) === cid);
          if (match) restored[match.id] = String(toDisplayValue(cpv, match.metadata));
        }
        const cprices = c['prices'];
        if (Array.isArray(cprices)) {
          for (const p of cprices) {
            if (typeof p !== 'object' || p === null) continue;
            const entry = p as Record<string, unknown>;
            const ptId = typeof entry['id'] === 'string' ? entry['id'] : '';
            const epv = typeof entry['price_value'] === 'number' ? entry['price_value'] : null;
            if (ptId && epv !== null) {
              const match = editable.find((o) => getSlotId(o.metadata, o.id) === ptId);
              if (match) restored[match.id] = String(toDisplayValue(epv, match.metadata));
            }
          }
        }
      }
    } else if (editable.length === 1) {
      // Single slot: read price_value
      const pv = piObj['price_value'];
      const opt = editable[0];
      if (typeof pv === 'number' && opt) {
        restored[opt.id] = String(toDisplayValue(pv, opt.metadata));
      }
    } else if (editable.length > 1) {
      // Multi slot (single-component): read prices array
      const prices = piObj['prices'];
      if (Array.isArray(prices)) {
        for (const p of prices) {
          if (typeof p === 'object' && p !== null) {
            const entry = p as Record<string, unknown>;
            const ptId = typeof entry['id'] === 'string' ? entry['id'] : '';
            const pv = typeof entry['price_value'] === 'number' ? entry['price_value'] : null;
            if (ptId && pv !== null) {
              // Match back to option by price_template_id
              const match = editable.find((o) => getSlotId(o.metadata, o.id) === ptId);
              if (match) restored[match.id] = String(toDisplayValue(pv, match.metadata));
            }
          }
        }
      }
    }
    return restored;
  });

  const allValid = useMemo(() => {
    if (options.options.length === 0) return false;
    // Formula-driven slots don't need user input — only check editable slots
    if (editableOptions.length === 0) return true;
    return editableOptions.every((opt) => {
      const raw = values[opt.id];
      if (raw === undefined || raw === '') return false;
      const num = Number(raw);
      return !Number.isNaN(num) && num >= 0;
    });
  }, [options.options, editableOptions, values]);

  const handleChange = useCallback((optionId: string, value: string) => {
    setValues((prev) => ({ ...prev, [optionId]: value }));
  }, []);

  const handleSubmit = useCallback(() => {
    if (!allValid) return;

    // Detect multi-component templates (more than one distinct component_template_id)
    const componentIds = new Set(options.options.map((o) => getComponentId(o.metadata)));
    const isMultiComponent = componentIds.size > 1;

    if (isMultiComponent) {
      // Group options by component, then build per-component price entries
      const compMap = new Map<string, StepOption[]>();
      for (const opt of options.options) {
        const cid = getComponentId(opt.metadata);
        let arr = compMap.get(cid);
        if (!arr) { arr = []; compMap.set(cid, arr); }
        arr.push(opt);
      }

      const components = [...compMap.entries()].map(([cid, slots]) => {
        const editable = slots.filter(isEditableSlot);
        if (editable.length === 0) {
          return { id: cid, prices: [] as { id: string; price_value: number }[] };
        }
        return {
          id: cid,
          prices: editable.map((opt) => ({
            id: getSlotId(opt.metadata, opt.id),
            price_value: fromDisplayValue(Number(values[opt.id]), opt.metadata),
          })),
        };
      });

      onSubmit({ components, vat_included: vatIncluded });
      return;
    }

    // Single-component backward-compatible formats
    const [onlyOpt] = editableOptions;
    if (!onlyOpt) {
      onSubmit({ prices: [], vat_included: vatIncluded });
    } else if (editableOptions.length === 1 && options.options.length === 1) {
      onSubmit({
        price_value: fromDisplayValue(Number(values[onlyOpt.id]), onlyOpt.metadata),
        vat_included: vatIncluded,
      });
    } else {
      const prices = editableOptions.map((opt) => ({
        id: getSlotId(opt.metadata, opt.id),
        price_value: fromDisplayValue(Number(values[opt.id]), opt.metadata),
      }));
      onSubmit({ prices, vat_included: vatIncluded });
    }
  }, [allValid, editableOptions, options.options, values, vatIncluded, onSubmit]);

  return (
    <>
      <StepHeading
        title={t('steps.retailer_custom_prices.title')}
        subtitle={t('steps.retailer_custom_prices.subtitle')}
        icon={getStepIcon(stepId)}
      />

      <div
        role="radiogroup"
        aria-label={t('steps.retailer_custom_prices.vatModeLegend')}
        className={styles['vatModeGroup']}
      >
        <button
          type="button"
          role="radio"
          aria-checked={!vatIncluded}
          className={styles['vatModeOption']}
          onClick={() => setVatIncluded(false)}
          disabled={isSubmitting}
        >
          {t('steps.retailer_custom_prices.vatExcludedOption')}
        </button>
        <button
          type="button"
          role="radio"
          aria-checked={vatIncluded}
          className={styles['vatModeOption']}
          onClick={() => setVatIncluded(true)}
          disabled={isSubmitting}
        >
          {t('steps.retailer_custom_prices.vatIncludedOption')}
        </button>
      </div>

      <div className={styles['groups']}>
        {groups.map((group) => (
          <section key={group.componentName} className={styles['group']}>
            <h3 className={styles['groupHeading']}>{group.componentName}</h3>
            <div className={styles['slots']}>
              {group.slots.map((slot) => {
                const meta = slot.metadata;
                const hasTou = meta['has_tou'] === true;
                const touType = typeof meta['tou_type'] === 'string' ? meta['tou_type'] : null;
                const touLabel = touType ? t(`common.tou.${touType}`) : null;
                const showLabel = slot.name !== group.componentName;
                const hasFormula = meta['has_formula'] === true;
                const unitHint = getUnitHint(meta);
                const inputId = `price-${slot.id}`;

                return (
                  <div key={slot.id} className={styles['slot']}>
                    {(showLabel || hasTou) && (
                      <div className={styles['slotHeader']}>
                        {showLabel && (
                          <label htmlFor={hasFormula ? undefined : inputId} className={styles['slotLabel']}>
                            {slot.name}
                          </label>
                        )}
                        {hasTou && touLabel && (
                          <span className={styles['touBadge']} data-tou-type={touType}>
                            {touLabel}
                          </span>
                        )}
                      </div>
                    )}
                    {hasFormula ? (
                      <div className={styles['formulaInfo']}>
                        <span>{t('steps.retailer_custom_prices.formulaAuto')}</span>
                      </div>
                    ) : (
                      <div className={styles['inputRow']}>
                        <input
                          id={inputId}
                          type="number"
                          className={styles['input']}
                          step="0.001"
                          min="0"
                          placeholder="0.00"
                          value={values[slot.id] ?? ''}
                          onChange={(e) => handleChange(slot.id, e.target.value)}
                          disabled={isSubmitting}
                          aria-label={`${slot.name} — ${unitHint ?? 'price'}`}
                        />
                        {unitHint && <span className={styles['unitHint']}>{unitHint}</span>}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </div>

      <FooterActions
        primaryLabel={t('common.next')}
        onPrimary={handleSubmit}
        isLoading={isSubmitting}
        disabled={!allValid}
      />
    </>
  );
});
